import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Search, Gauge, Copy, Lock, Loader2, CheckCircle2, AlertTriangle, XCircle, Link2, Upload, Trash2, Plug, ExternalLink } from "lucide-react";
import { useTr } from "@/i18n/translator";

const TABS = [
  { id: "blog", label: "Blog Post Generator", icon: FileText },
  { id: "keywords", label: "Keyword Research", icon: Search },
  { id: "audit", label: "On-Page Audit", icon: Gauge },
  { id: "publish", label: "Auto-Publish", icon: Upload },
];

export default function SEO() {
  const tr = useTr();
  const [tab, setTab] = useState("blog");
  const [gated, setGated] = useState(false);

  return (
    <div className="space-y-6" data-testid="seo-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">DOSSIER · SEO · WEB OPTIMIZATION</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Módulo SEO")}</h1>
        <p className="mt-3 font-mono-data text-sm text-[#5C5C5C] max-w-2xl">
          {tr("Genera artículos optimizados, encuentra keywords que valen la pena, y audita tu sitio web en minutos.")}
          <span className="ml-2 font-mono-mark text-[10px] bg-[#FFD600] px-2 py-0.5">{tr("PRO · AGENCIA")}</span>
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#0A0A0A] flex gap-0" data-testid="seo-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-3 font-mono-mark text-[10px] border-t border-l border-r border-[#0A0A0A] ${tab === id ? "bg-white text-[#E53935] -mb-px border-b-white" : "bg-[#F4F4F0] text-[#5C5C5C]"}`}
            data-testid={`seo-tab-${id}`}
          >
            <Icon className="w-3 h-3" /> {label.toUpperCase()}
          </button>
        ))}
      </div>

      {gated ? (
        <div className="bg-white border border-[#0A0A0A] p-10 text-center" data-testid="seo-gated">
          <Lock className="w-12 h-12 mx-auto text-[#5C5C5C]" />
          <h2 className="font-display text-2xl mt-3">{tr("Módulo SEO · solo Pro y Agencia")}</h2>
          <p className="mt-2 font-mono-data text-sm text-[#5C5C5C]">
            {tr("Actualiza tu plan para desbloquear blog generator, keyword research y on-page audit.")}
          </p>
        </div>
      ) : (
        <>
          {tab === "blog" && <BlogGenerator onGated={() => setGated(true)} />}
          {tab === "keywords" && <KeywordResearch onGated={() => setGated(true)} />}
          {tab === "audit" && <OnPageAudit onGated={() => setGated(true)} />}
          {tab === "publish" && <AutoPublish onGated={() => setGated(true)} />}
        </>
      )}
    </div>
  );
}

// ---------- Blog Post Generator ----------
function BlogGenerator({ onGated }) {
  const tr = useTr();
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [language, setLanguage] = useState("es-MX");
  const [tone, setTone] = useState("profesional");
  const [words, setWords] = useState(900);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!topic.trim()) return toast.error(tr("Describe el tema del artículo"));
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/seo/blog-post", {
        topic,
        keywords: keywords.split(",").map((s) => s.trim()).filter(Boolean),
        language, tone, target_words: Number(words),
      });
      setResult(data);
      toast.success(`Artículo generado · ${data.word_count} palabras`);
    } catch (e) {
      if (e.response?.status === 402) onGated();
      else toast.error(e.response?.data?.detail || "Error al generar");
    } finally { setLoading(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado"); };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="seo-blog">
      <div className="bg-white border border-[#0A0A0A] p-5 space-y-3">
        <Field label={tr("TEMA")}><textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={3} className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" placeholder={tr("Ej: Cómo elegir el mejor CRM para PyMEs en 2026")} data-testid="blog-topic" /></Field>
        <Field label={tr("KEYWORDS (SEPARADAS POR COMA)")}><input value={keywords} onChange={(e) => setKeywords(e.target.value)} className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" placeholder="crm pymes, mejor crm, software ventas" data-testid="blog-keywords" /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label={tr("IDIOMA")}>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="w-full bg-white border border-[#0A0A0A] px-2 py-2 font-mono-data text-sm" data-testid="blog-language">
              <option value="es-MX">{tr("Español (MX)")}</option>
              <option value="en">English</option>
              <option value="pt-BR">Português (BR)</option>
            </select>
          </Field>
          <Field label={tr("TONO")}>
            <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full bg-white border border-[#0A0A0A] px-2 py-2 font-mono-data text-sm" data-testid="blog-tone">
              <option value="profesional">{tr("Profesional")}</option>
              <option value="casual">{tr("Casual")}</option>
              <option value="experto">{tr("Experto")}</option>
              <option value="persuasivo">{tr("Persuasivo")}</option>
            </select>
          </Field>
          <Field label={tr("PALABRAS")}>
            <input type="number" value={words} onChange={(e) => setWords(e.target.value)} min={300} max={2500} step={100} className="w-full bg-white border border-[#0A0A0A] px-2 py-2 font-mono-data text-sm" data-testid="blog-words" />
          </Field>
        </div>
        <button onClick={generate} disabled={loading} className="w-full bg-[#E53935] text-white px-4 py-3 font-mono-mark text-xs border border-[#E53935] hover-brutal disabled:opacity-50" data-testid="blog-generate">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> {tr("GENERANDO…")}</> : tr("GENERAR ARTÍCULO OPTIMIZADO")}
        </button>
      </div>
      {result && (
        <div className="bg-white border border-[#0A0A0A] p-5 space-y-3" data-testid="blog-result">
          <div className="flex items-center justify-between">
            <span className="font-mono-mark text-[9px] text-[#5C5C5C]">ARTÍCULO · {result.word_count} PALABRAS</span>
            <button onClick={() => copy(result.body_md)} className="font-mono-mark text-[9px] underline"><Copy className="w-3 h-3 inline" /> COPIAR MD</button>
          </div>
          <div className="bg-[#F4F4F0] border border-[#0A0A0A] p-3">
            <div className="font-mono-mark text-[9px] text-[#5C5C5C]">TITLE ({result.meta_title.length}/60)</div>
            <div className="font-display text-base mt-1">{result.meta_title}</div>
            <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-3">META DESCRIPTION ({result.meta_description.length}/160)</div>
            <div className="font-mono-data text-xs mt-1">{result.meta_description}</div>
            <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-3">SLUG</div>
            <code className="font-mono-data text-xs">/{result.slug}</code>
          </div>
          <div className="font-mono-mark text-[9px] text-[#5C5C5C]">OUTLINE</div>
          <ul className="font-mono-data text-xs list-disc pl-5 space-y-1">
            {result.outline.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
          <details className="border border-[#0A0A0A]">
            <summary className="cursor-pointer bg-[#0A0A0A] text-white px-3 py-2 font-mono-mark text-[10px]">CUERPO (MARKDOWN)</summary>
            <pre className="p-3 text-xs whitespace-pre-wrap max-h-96 overflow-y-auto">{result.body_md}</pre>
          </details>
          <details className="border border-[#0A0A0A]">
            <summary className="cursor-pointer bg-[#FFD600] px-3 py-2 font-mono-mark text-[10px]">SCHEMA.ORG JSON-LD</summary>
            <pre className="p-3 text-xs whitespace-pre-wrap">{JSON.stringify(result.json_ld, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

// ---------- Keyword Research ----------
function KeywordResearch({ onGated }) {
  const tr = useTr();
  const [seed, setSeed] = useState("");
  const [industry, setIndustry] = useState("");
  const [language, setLanguage] = useState("es-MX");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!seed.trim()) return toast.error(tr("Ingresa una keyword semilla"));
    setLoading(true);
    try {
      const { data } = await api.post("/seo/keywords", { seed, industry, language });
      setResult(data);
    } catch (e) {
      if (e.response?.status === 402) onGated();
      else toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="seo-keywords">
      <div className="bg-white border border-[#0A0A0A] p-5 grid md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
        <Field label="KEYWORD SEMILLA"><input value={seed} onChange={(e) => setSeed(e.target.value)} placeholder={tr("café de especialidad")} className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="kw-seed" /></Field>
        <Field label="INDUSTRIA"><input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder={tr("cafeterías")} className="w-40 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="kw-industry" /></Field>
        <Field label={tr("IDIOMA")}>
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className="bg-white border border-[#0A0A0A] px-2 py-2 font-mono-data text-sm" data-testid="kw-language">
            <option value="es-MX">{tr("Español")}</option>
            <option value="en">English</option>
            <option value="pt-BR">Português</option>
          </select>
        </Field>
        <button onClick={run} disabled={loading} className="bg-[#0A0A0A] text-white px-5 py-2 font-mono-mark text-xs border border-[#0A0A0A] disabled:opacity-50" data-testid="kw-run">
          {loading ? "BUSCANDO…" : "BUSCAR"}
        </button>
      </div>

      {result && (
        <div className="bg-white border border-[#0A0A0A] overflow-x-auto" data-testid="kw-result">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white font-mono-mark text-[10px]">
              <tr>
                <th className="text-left p-3">KEYWORD</th>
                <th className="text-left p-3">INTENT</th>
                <th className="text-left p-3">DIFICULTAD</th>
                <th className="text-left p-3">VOLUMEN EST.</th>
                <th className="text-left p-3">PRIORIDAD</th>
              </tr>
            </thead>
            <tbody className="font-mono-data">
              {result.suggestions.map((k, i) => (
                <tr key={i} className="border-t border-[#0A0A0A]/20" data-testid={`kw-row-${i}`}>
                  <td className="p-3 font-display">{k.keyword}</td>
                  <td className="p-3 text-xs uppercase">{k.intent}</td>
                  <td className="p-3"><Pill text={k.difficulty} map={{ baja: "bg-[#FFD600]", media: "bg-[#0A0A0A] text-[#FFD600]", alta: "bg-[#E53935] text-white" }} /></td>
                  <td className="p-3 text-xs">{k.est_volume}</td>
                  <td className="p-3"><Pill text={k.priority} map={{ alta: "bg-[#E53935] text-white", media: "bg-[#FFD600]", baja: "bg-[#F4F4F0]" }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- On-Page Audit ----------
function OnPageAudit({ onGated }) {
  const tr = useTr();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!url.trim()) return toast.error(tr("Ingresa una URL"));
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/seo/audit", { url });
      setResult(data);
    } catch (e) {
      if (e.response?.status === 402) onGated();
      else toast.error(e.response?.data?.detail || "Error al auditar");
    } finally { setLoading(false); }
  };

  const scoreColor = result?.score >= 80 ? "bg-[#FFD600]" : result?.score >= 60 ? "bg-[#FFD600]" : "bg-[#E53935] text-white";

  return (
    <div className="space-y-4" data-testid="seo-audit">
      <div className="bg-white border border-[#0A0A0A] p-5 flex gap-3">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://tusitio.com" className="flex-1 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="audit-url" />
        <button onClick={run} disabled={loading} className="bg-[#0A0A0A] text-white px-5 py-2 font-mono-mark text-xs border border-[#0A0A0A] disabled:opacity-50" data-testid="audit-run">
          {loading ? "ANALIZANDO…" : "AUDITAR"}
        </button>
      </div>
      {result && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="audit-result">
          <div className={`border border-[#0A0A0A] p-6 text-center ${scoreColor}`}>
            <div className="font-mono-mark text-[10px]">SCORE SEO</div>
            <div className="font-display text-7xl leading-none mt-2">{result.score}</div>
            <div className="font-mono-mark text-[9px] mt-2">/ 100</div>
          </div>
          <div className="md:col-span-2 bg-white border border-[#0A0A0A] p-5 space-y-2">
            <Stat label={tr("URL")} value={result.url} />
            <Stat label="HTTP" value={result.status_code} />
            <Stat label="Load time" value={`${result.load_ms} ms`} />
            <Stat label="Title" value={`${result.title || "(vacío)"} (${result.title?.length || 0}/60)`} />
            <Stat label="Meta description" value={`${result.meta_description || "(vacía)"} (${result.meta_description?.length || 0}/160)`} />
            <Stat label="H1" value={`${result.h1_count} · "${result.h1_first || "-"}"`} />
            <Stat label="Imágenes sin alt" value={`${result.images_missing_alt} / ${result.images_total}`} />
          </div>
          <div className="md:col-span-3 bg-white border border-[#0A0A0A]">
            <div className="px-5 py-3 font-mono-mark text-[10px] bg-[#0A0A0A] text-white">ISSUES · {result.issues.length}</div>
            <ul>
              {result.issues.length === 0 && (
                <li className="p-5 text-center font-mono-data text-sm text-[#5C5C5C] flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#E53935]" /> {tr("Sin issues críticos")}</li>
              )}
              {result.issues.map((iss, i) => (
                <li key={i} className="px-5 py-3 border-t border-[#0A0A0A]/20 flex items-start gap-3" data-testid={`audit-issue-${i}`}>
                  {iss.severity === "high" ? <XCircle className="w-4 h-4 text-[#E53935] shrink-0 mt-0.5" /> : iss.severity === "medium" ? <AlertTriangle className="w-4 h-4 text-[#0A0A0A] shrink-0 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 text-[#5C5C5C] shrink-0 mt-0.5" />}
                  <div>
                    <div className="font-mono-mark text-[9px] text-[#5C5C5C]">{iss.severity.toUpperCase()} · {iss.category.toUpperCase()}</div>
                    <div className="font-mono-data text-sm">{iss.message}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="font-mono-mark text-[9px] text-[#5C5C5C]">{label}</span><div className="mt-1">{children}</div></label>;
}
function Pill({ text, map }) {
  const cls = map[text] || "bg-[#F4F4F0]";
  return <span className={`font-mono-mark text-[9px] px-2 py-0.5 ${cls}`}>{(text || "").toUpperCase()}</span>;
}
function Stat({ label, value }) {
  return <div className="flex gap-3 items-baseline"><span className="font-mono-mark text-[10px] text-[#5C5C5C] shrink-0 w-28">{label.toUpperCase()}</span><span className="font-mono-data text-sm truncate">{value || "-"}</span></div>;
}

// ---------- Auto Publish to CMS ----------
function AutoPublish({ onGated }) {
  const tr = useTr();
  const [connections, setConnections] = useState([]);
  const [posts, setPosts] = useState([]);
  const [provider, setProvider] = useState("wordpress");
  const [name, setName] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [username, setUsername] = useState("");
  const [credential, setCredential] = useState("");
  const [blogId, setBlogId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [selPost, setSelPost] = useState("");
  const [selConn, setSelConn] = useState("");
  const [publishStatus, setPublishStatus] = useState("draft");
  const [publishing, setPublishing] = useState(false);

  const loadAll = async () => {
    try {
      const [{ data: c }, { data: p }] = await Promise.all([
        api.get("/seo/cms/connections"),
        api.get("/seo/blog-posts"),
      ]);
      setConnections(c);
      setPosts(p);
      if (c[0]) setSelConn(c[0].id);
      if (p[0]) setSelPost(p[0].id);
    } catch (e) {
      if (e.response?.status === 402) onGated();
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const connect = async () => {
    if (!name || !siteUrl || !credential || (provider === "wordpress" && !username)) {
      return toast.error(tr("Completa todos los campos requeridos"));
    }
    setConnecting(true);
    try {
      await api.post("/seo/cms/connect", {
        provider, name, site_url: siteUrl, username: username || null, credential, blog_id: blogId || null,
      });
      toast.success(tr("Conexión verificada y guardada"));
      setName(""); setSiteUrl(""); setUsername(""); setCredential(""); setBlogId("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conexión falló");
    } finally { setConnecting(false); }
  };

  const removeConn = async (id) => {
    if (!window.confirm("¿Eliminar esta conexión?")) return;
    await api.delete(`/seo/cms/connections/${id}`);
    toast.success(tr("Conexión eliminada"));
    loadAll();
  };

  const publish = async () => {
    if (!selPost || !selConn) return toast.error(tr("Elige un blog post y una conexión"));
    setPublishing(true);
    try {
      const { data } = await api.post("/seo/cms/publish", {
        blog_post_id: selPost, connection_id: selConn, status: publishStatus,
      });
      toast.success(`Publicado como ${publishStatus === "publish" ? "PUBLICADO" : "BORRADOR"}`);
      if (data.published_url) {
        window.open(data.published_url, "_blank");
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al publicar");
    } finally { setPublishing(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="seo-publish">
      {/* Connect form */}
      <div className="bg-white border border-[#0A0A0A] p-5 space-y-3">
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("CONECTAR UN CMS · WORDPRESS / SHOPIFY")}</div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setProvider("wordpress")} className={`px-3 py-2 font-mono-mark text-[10px] border ${provider === "wordpress" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]"}`} data-testid="provider-wp">
            WORDPRESS
          </button>
          <button onClick={() => setProvider("shopify")} className={`px-3 py-2 font-mono-mark text-[10px] border ${provider === "shopify" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]"}`} data-testid="provider-shopify">
            SHOPIFY
          </button>
        </div>
        <Field label="NOMBRE"><input value={name} onChange={(e) => setName(e.target.value)} placeholder={tr("Mi blog WordPress")} className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="cms-name" /></Field>
        <Field label={provider === "wordpress" ? "URL DEL SITIO" : "MYSHOP.MYSHOPIFY.COM"}>
          <input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder={provider === "wordpress" ? "https://misitio.com" : "mishop.myshopify.com"} className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="cms-site" />
        </Field>
        {provider === "wordpress" && (
          <>
            <Field label="USUARIO WP"><input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="cms-username" /></Field>
            <Field label="APPLICATION PASSWORD"><input type="password" value={credential} onChange={(e) => setCredential(e.target.value)} placeholder="xxxx xxxx xxxx xxxx" className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="cms-credential" /></Field>
            <div className="font-mono-mark text-[9px] text-[#5C5C5C]">
              {tr("GENERA UN APP PASSWORD EN: USUARIOS → PERFIL → APPLICATION PASSWORDS")}
            </div>
          </>
        )}
        {provider === "shopify" && (
          <>
            <Field label="ADMIN API ACCESS TOKEN"><input type="password" value={credential} onChange={(e) => setCredential(e.target.value)} placeholder="shpat_..." className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="cms-credential" /></Field>
            <Field label="BLOG ID (OPCIONAL)"><input value={blogId} onChange={(e) => setBlogId(e.target.value)} placeholder={tr("Si no, usamos el primero disponible")} className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="cms-blog-id" /></Field>
            <div className="font-mono-mark text-[9px] text-[#5C5C5C]">
              {tr("CREA UNA CUSTOM APP EN SETTINGS → APPS → DEVELOP APPS. SCOPE: write_content")}
            </div>
          </>
        )}
        <button onClick={connect} disabled={connecting} className="w-full bg-[#E53935] text-white px-4 py-3 font-mono-mark text-xs border border-[#E53935] hover-brutal disabled:opacity-50 flex items-center justify-center gap-2" data-testid="cms-connect">
          {connecting ? <><Loader2 className="w-4 h-4 animate-spin" /> VERIFICANDO…</> : <><Plug className="w-4 h-4" /> {tr("CONECTAR Y GUARDAR")}</>}
        </button>
      </div>

      {/* Connections + Publish */}
      <div className="space-y-5">
        <div className="bg-white border border-[#0A0A0A]">
          <div className="px-4 py-2 border-b border-[#0A0A0A] font-mono-mark text-[10px] text-[#5C5C5C]">
            CONEXIONES ACTIVAS · {connections.length}
          </div>
          {connections.length === 0 ? (
            <div className="p-6 text-center font-mono-data text-sm text-[#5C5C5C]" data-testid="cms-empty">
              {tr("Aún no tienes CMS conectado. Conecta uno para publicar automáticamente.")}
            </div>
          ) : (
            <ul>
              {connections.map((c) => (
                <li key={c.id} className="px-4 py-3 border-b border-[#0A0A0A]/20 flex items-center justify-between gap-3" data-testid={`cms-conn-${c.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      <span className="font-display text-base">{c.name}</span>
                      <span className="font-mono-mark text-[9px] bg-[#FFD600] px-2 py-0.5">{c.provider.toUpperCase()}</span>
                    </div>
                    <div className="font-mono-data text-xs text-[#5C5C5C] mt-1 truncate">
                      {c.site_url}
                      {c.last_publish_at && ` · último publish ${new Date(c.last_publish_at).toLocaleDateString("es-MX")}`}
                    </div>
                  </div>
                  <button onClick={() => removeConn(c.id)} className="font-mono-mark text-[9px] border border-[#E53935] text-[#E53935] px-2 py-1 flex items-center gap-1" data-testid={`cms-del-${c.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Publisher */}
        <div className="bg-[#0A0A0A] text-white border border-[#0A0A0A] p-5 space-y-3">
          <div className="font-mono-mark text-[10px] text-[#FFD600]">{tr("PUBLICAR BLOG POST EN UN CLIC")}</div>
          <Field label="BLOG POST GENERADO">
            <select value={selPost} onChange={(e) => setSelPost(e.target.value)} className="w-full bg-white text-[#0A0A0A] border border-white/20 px-3 py-2 font-mono-data text-sm" data-testid="publish-post">
              <option value="">— Selecciona —</option>
              {posts.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.word_count}w</option>)}
            </select>
          </Field>
          <Field label="DESTINO">
            <select value={selConn} onChange={(e) => setSelConn(e.target.value)} className="w-full bg-white text-[#0A0A0A] border border-white/20 px-3 py-2 font-mono-data text-sm" data-testid="publish-conn">
              <option value="">— Selecciona —</option>
              {connections.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.provider}</option>)}
            </select>
          </Field>
          <Field label="ESTADO">
            <div className="flex gap-2">
              <button onClick={() => setPublishStatus("draft")} className={`flex-1 px-3 py-2 font-mono-mark text-[10px] border ${publishStatus === "draft" ? "bg-[#FFD600] text-[#0A0A0A] border-[#FFD600]" : "border-white/40 text-white"}`} data-testid="publish-status-draft">
                BORRADOR
              </button>
              <button onClick={() => setPublishStatus("publish")} className={`flex-1 px-3 py-2 font-mono-mark text-[10px] border ${publishStatus === "publish" ? "bg-[#E53935] text-white border-[#E53935]" : "border-white/40 text-white"}`} data-testid="publish-status-publish">
                PUBLICAR AHORA
              </button>
            </div>
          </Field>
          <button onClick={publish} disabled={publishing || !selPost || !selConn} className="w-full bg-[#FFD600] text-[#0A0A0A] px-4 py-3 font-mono-mark text-xs border border-[#FFD600] hover-brutal disabled:opacity-50 flex items-center justify-center gap-2" data-testid="publish-go">
            {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> PUBLICANDO…</> : <><ExternalLink className="w-4 h-4" /> PUBLICAR EN CMS</>}
          </button>
          <div className="font-mono-mark text-[9px] text-white/60">
            {tr("CIA MARKETING® CONVIERTE EL MD A HTML E INYECTA SCHEMA.ORG JSON-LD + META TAGS (YOAST COMPATIBLE).")}
          </div>
        </div>
      </div>
    </div>
  );
}
